<template>
  <div class="m-3" style="height: 90vh" :dir="$ctx.getDir()">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <template v-if="$can('read', '/sales/orders')">
        <el-select class="input-label col-span-12 md:col-span-2" v-model="selectedValue">
          <el-option
            key="quickView"
            :label="$t('views.common.listView.quickView')"
            value="quickView"
          >
          </el-option>
          <el-option key="allItems" :label="$t('views.common.listView.allItems')" value="allItems">
          </el-option>
          <el-option
            key="export"
            :label="$t('views.common.listView.selectedRows.export')"
            value="export"
          >
          </el-option>
          <!-- <template v-if="$can('delete', '/sales/orders')">
            <el-option
              key="delete"
              :label="$t('views.common.listView.selectedRows.delete')"
              value="delete"
            >
            </el-option>
          </template> -->
        </el-select>
      </template>

      <el-input
        :placeholder="t('searchReceipt')"
        class="w-full col-span-12 md:col-span-8"
        :prefix-icon="Search"
        v-model="searchTerm"
        ref="searchBar"
      />
      <div class="col-span-12 md:col-span-2">
        <template v-if="true">
          <horizontal-button
            :title="$t('actions.newSale')"
            @click.prevent="newSale()"
          />
        </template>
      </div>
    </div>


    <div class="my-2 lg:mb-2 w-full">

      <div class="grid grid-cols-3 gap-2 items-center">
        <ToggleGroup v-model="orderTypeFilter" class="gap-2 flex-wrap lg:flex-nowrap col-span-2" type="multiple">
          <ToggleGroupItem value="unprocessed" class="data-[state=on]:bg-white data-[state=on]:border-[#FCC5C5] data-[state=on]:text-gray-800 hover:bg-[#FCC5C5] bg-[#FCC5C5]">
            {{t('orderStatus.unprocessed')}}
          </ToggleGroupItem>
          <ToggleGroupItem value="processing" class="data-[state=on]:bg-white data-[state=on]:border-[#FFE072] data-[state=on]:text-gray-800 hover:bg-[#FFE072] bg-[#FFE072]">
            {{t('orderStatus.processing')}}
          </ToggleGroupItem>
          <ToggleGroupItem value="ready" class="data-[state=on]:bg-white data-[state=on]:border-[#B8DAFC] data-[state=on]:text-gray-800 hover:bg-[#B8DAFC] bg-[#B8DAFC]">
            {{t('orderStatus.ready')}}
          </ToggleGroupItem>
          <ToggleGroupItem value="on_the_way" class="data-[state=on]:bg-white data-[state=on]:border-[#D0C5F4] data-[state=on]:text-gray-800 hover:bg-[#D0C5F4] bg-[#D0C5F4]">
            {{t('orderStatus.onTheWay')}}
          </ToggleGroupItem>
          <ToggleGroupItem value="delivered" class="data-[state=on]:bg-white data-[state=on]:border-[#D1F1BB] data-[state=on]:text-gray-800 hover:bg-[#D1F1BB] bg-[#D1F1BB]">
            {{t('orderStatus.delivered')}}
          </ToggleGroupItem>

        </ToggleGroup>
        <div class="flex items-center gap-2">
          <span>|</span>
          <ToggleGroup v-model="orderFilter" class="gap-2 flex-wrap lg:flex-nowrap" type="single">
            <ToggleGroupItem value="sales" class="rtl:w-[100px] data-[state=on]:bg-white data-[state=on]:border-gray-500 data-[state=on]:text-gray-800 bg-[#D1F1BB]">
              {{t('paid')}}
            </ToggleGroupItem>
            <ToggleGroupItem value="orders" class="rtl:w-[120px] data-[state=on]:bg-white data-[state=on]:border-gray-500 data-[state=on]:text-gray-800 bg-[#FCC5C5]">
              {{t('unpaid')}}
            </ToggleGroupItem>
          </ToggleGroup>
          <button
            type="button"
            class="px-3 py-1 rounded-md text-sm border whitespace-nowrap"
            :class="sortByDueDate
              ? 'bg-white border-gray-500 text-gray-800'
              : 'bg-[#E8EEF6] border-transparent text-gray-800 hover:bg-white hover:border-gray-500'"
            @click="sortByDueDate = !sortByDueDate"
          >
            {{ t('sortByDueDate') }}
          </button>
        </div>
      </div>
    </div>

    <ag-grid-vue
      v-if="rowModelType"
      class="ag-theme-alpine mt-4 md:mt-0"
      :columnDefs="columnDefs"
      rowSelection="multiple"
      :enable-rtl="$ctx.getDir() === 'rtl'"
      style="height: 80vh"
      @cell-double-clicked="onCellDoubleClicked"
      :defaultColDef="defaultColDef"
      :rowData="rowData"
      :pagination="selectedValue == 'allItems'"
      :paginationAutoPageSize="selectedValue == 'allItems'"
      :onGridReady="onGridReady"
      :components="components"
      :rowBuffer="rowBuffer"
      :rowModelType="rowModelType"
      :cacheBlockSize="cacheBlockSize"
      :cacheOverflowSize="cacheOverflowSize"
      :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
      :infiniteInitialRowCount="infiniteInitialRowCount"
      :maxBlocksInCache="maxBlocksInCache"
      :suppressDragLeaveHidesColumns="true"
      :suppressColumnMoveAnimation="true"
      @column-moved="onColumnStateChanged"
      @column-resized="onColumnStateChanged"
    >
    </ag-grid-vue>

    <div class="mt-2">{{ $t('views.common.listView.count') }}: {{ count }}</div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'

// components
import { AgGridVue } from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  IDatasource,
  IGetRowsParams,
  ValueFormatterParams,
  ModuleRegistry
} from 'ag-grid-community'

import { i18nOrderMessages, Order, OrdersPaginated } from '@/models/sales/Order'
import { useI18n } from 'vue-i18n'
import { logError } from '@/utils/log'
import { formatPostgresDate } from '@/utils/date'
import { formatMoney } from '@/utils/money'
import { orderDueOnCellStyle } from '@/utils/orderDueOn'
import HorizontalButton from '@/components/HorizontalButton.vue'
import { Search } from '@element-plus/icons-vue'

import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model'
import { onBeforeMount } from 'vue'
import BirthdayMessageCellRenderer
  from "@/views/reports/v2/customers/BirthdayMessageCellRenderer.vue";
import OrderStatusCellRenderer from "@/views/sales/OrderStatusCellRenderer.vue";
import PaymentStatusCellRenderer
  from "@/views/sales/PaymentStatusCellRenderer.vue";
ModuleRegistry.registerModules([InfiniteRowModelModule])
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {handleNewSale} from "@/lib/utils";
import InsurancePaymentStatusCellRenderer
  from "@/views/companies/insurances/InsurancePaymentStatusCellRenderer.vue";

export default defineComponent({
  name: 'orders',

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...i18nOrderMessages.en,
          searchReceipt: 'Enter receipt number',
          sales: 'Sales',
          orders: 'Orders',
          paid: 'Paid',
          unpaid: 'Unpaid'
        },
        ar: {
          ...i18nOrderMessages.ar,
          searchReceipt: 'ادخل رقم الفاتورة',
          sales: 'المبيعات',
          orders: 'الطلبات',
          paid: 'مدفوع',
          unpaid: 'غير مدفوع'
        }
      }
    })

    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)
    const orderTypeFilter = ref<Array<string>>([])
    const orderFilter = ref<string>("")

    onBeforeMount(() => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10
    })

    return {
      t,
      Search,
      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache,
      orderTypeFilter,
      orderFilter
    }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: Order[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}
    const datasource: IDatasource = {} as IDatasource
    const components = {
      loadingRenderer: (params: ValueFormatterParams) => {
        return params.value !== undefined
          ? params.value
          : '<img src="https://www.ag-grid.com/example-assets/loading.gif">'
      }
    }

    return {
      dateRange: '',
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,
      components,
      datasource,
      searchTerm: '',
      selectedValue: 'quickView',
      sortByDueDate: false,
      gridApi: undefined as GridApi | undefined,
      count: 0
    }
  },

  components: {
    AgGridVue,
    HorizontalButton,
    ToggleGroup,
    ToggleGroupItem
  },

  async beforeMount() {
    this.defaultColDef = {
      resizable: true,
      minWidth: 50,
      sortable: false,
      filter: false
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },

      {
        headerName: this.t('orderNumber'),
        field: 'externalID',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        lockPosition: true,
        width: 140
      },
      {
        headerName: this.$t('models.common.date'),
        field: 'createdAt',
        cellStyle: { direction: 'ltr' },
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          if (order.createdAt) {
            const createdAt = new Date(order.createdAt)
            return formatPostgresDate(String(createdAt), 'DD/MM/YYYY')
          }
          return ''
        },
        width: 140
      },
      {
        headerName: this.$t('models.common.time'),
        field: 'createdAt',
        cellStyle: { direction: 'ltr' },
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          if (order.createdAt) {
            const createdAt = new Date(order.createdAt)
            return formatPostgresDate(String(createdAt), 'hh:mm A')
          }
          return ''
        },
        width: 100
      },
      {
        headerName: this.t('orderType'),
        width: 100,
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          switch (order.orderType) {
            case 'direct':
              return this.t('orderTypes.direct');
            case 'pick_up':
              return this.t('orderTypes.pickUp');
            case 'delivery':
              return this.t('orderTypes.delivery');
          }
        },
      },
      {
        headerName: this.t('orderState'),
        cellRenderer: OrderStatusCellRenderer,
        cellRendererParams: {
          // handleCheckChange: handleCheckChange
        }
      },
      {
        headerName: this.t('customerPaymentState'),
        cellRenderer: PaymentStatusCellRenderer,
      },
      {
        headerName: this.t('paymentTerms'),
        width: 150,
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          switch (order.invoice.paymentType) {
            case 'direct':
              return this.t('invoiceTypes.full');
            case 'layaway':
              return this.t('invoiceTypes.layaway');
            case 'credit_sale':
              return this.t('invoiceTypes.credit');
            case 'cash_on_delivery':
              return this.t('invoiceTypes.cashOnDelivery');
          }
        },
      },
      {
        headerName: this.t('dueOn'),
        field: 'invoice.billDueDate',
        width: 130,
        cellStyle: (params) => {
          const order = params.node?.data as Order
          if (!order?.invoice) return {}
          return orderDueOnCellStyle(order.invoice.billDueDate, order.invoice.totalBalanceAmount)
        },
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order?.invoice?.billDueDate) return ''
          return formatPostgresDate(String(order.invoice.billDueDate), 'DD/MM/YYYY')
        }
      },
      {
        headerName: this.t('insurancePaymentState'),
        cellRenderer: InsurancePaymentStatusCellRenderer,
      },
      {
        headerName: this.t('total'),
        field: 'invoice.totalDueAmount',
        valueGetter: ({ data }) => {
          const order = data as Order
          if (!order) return ''
          return order.invoice?.totalDueAmount != undefined
            ? formatMoney(order.invoice?.totalDueAmount + order.invoice?.insuranceTotalDueAmount, order.invoice.currency)
            : ''
        }
      },
      {
        headerName: this.t('customerPaid'),
        field: 'invoice.totalPaidAmount',
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''

          return formatMoney(order.invoice.totalPaidAmount ?? 0, order.invoice.currency)
        }
      },
      {
        headerName: this.t('insurancePaid'),
        field: 'invoice.totalPaidAmount',
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''

          return formatMoney(order.invoice.insuranceTotalPaidAmount ?? 0, order.invoice.currency)
        }
      },
      {
        headerName: this.t('balance'),
        field: 'invoice.totalBalanceAmount',
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          return formatMoney(order.invoice?.totalBalanceAmount + order.invoice?.insuranceTotalBalanceAmount ?? 0, order.invoice.currency)
        }
      },
      {
        headerName: this.t('user'),
        field: 'user.fullName',
        cellStyle: { textAlign: 'center' }
      },
      {
        headerName: this.t('salesRep'),
        field: 'invoice.salesRep.fullName',
        cellStyle: { textAlign: 'center' },
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          if (!order.invoice.salesRep) return '';
          return `${order.invoice.salesRep.name}`
        }
      },
      {
        headerName: this.t('customer'),
        field: 'invoice.customer.contactDisplayName',
        cellStyle: { textAlign: 'center' }
      },
      // {
      //   headerName: this.t('isDelivered'),
      //   field: 'isDelivered',
      //   valueFormatter: ({ data }) => {
      //     const order = data as Order
      //     if (!order) return ''
      //     return order.isDelivered ? '✔' : '✖'
      //   },
      //   cellStyle: (params) => {
      //     const base = { textAlign: 'center', backgroundColor: 'transparent', color: 'black' }
      //     const order = params.node?.data as Order
      //     if (!order) return base
      //
      //     if (order.isDelivered) {
      //       base['color'] = 'green'
      //     } else {
      //       base['color'] = 'red'
      //     }
      //
      //     return base
      //   }
      // },
      {
        headerName: this.t('pouchID'),
        field: 'invoice.pouchID',
        hide: true,
        cellStyle: { textAlign: 'center' }
      }
    ]

    try {
      this.count = (await this.$http.get<{ count: number }>('/helpers/count?model=orders')).count
    } catch (error) {
      this.$alertModal.showDanger({ title: error.title, body: error.body })
    }
  },

  mounted() {
    // this.gridApi?.sizeColumnsToFit()
  },

  watch: {
    orderTypeFilter: {
      handler() {
       this.updateOrderStatusFilter();
      },
    },
    orderFilter: {
      handler() {
       this.updateOrderStatusFilter();
      },
    },
    sortByDueDate: {
      handler() {
       this.updateOrderStatusFilter();
      },
    },
    searchTerm() {
      this.filterItems()
    },
    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export') {
        return
      }

      if (this.selectedValue === 'delete' || this.selectedValue === 'export') {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            // send request to archive
            await this.delete(selected)
          } else {
            this.gridApi?.exportDataAsCsv({ onlySelected: true })
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // deselect
          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    }
  },

  methods: {
    updateOrderStatusFilter() {
      if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    },
    async updateRowData() {
      this.gridApi?.showLoadingOverlay()

      const urlParams = new URLSearchParams()
      if (this.orderTypeFilter.length) {
        urlParams.set('order_status', this.orderTypeFilter.join(','))
      }

      if (this.orderFilter) {
        urlParams.set('order_filter', this.orderFilter)
      }

      if (this.sortByDueDate) {
        urlParams.set('sort_by', 'due_date')
      }

      try {
        const data = await this.$http.get<Order[]>(Order.ENDPOINT + `?${urlParams.toString()}`)
        this.rowData = data.map((order) => Order.from(order))
        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }
      this.gridApi?.hideOverlay()
    },

    getDatasource() {
      const dataSource = {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          const urlParams = new URLSearchParams()
          if (this.orderTypeFilter.length) {
            urlParams.set('order_status', this.orderTypeFilter.join(','))
          }

          if (this.orderFilter) {
            urlParams.set('order_filter', this.orderFilter)
          }

          if (this.sortByDueDate) {
            urlParams.set('sort_by', 'due_date')
          }

          let url = `${Order.ENDPOINT}?startRow=${params.startRow}&endRow=${params.endRow}&${urlParams.toString()}`
          if (params.sortModel.length) {
            url += `&colId=${params.sortModel[0].colId}&sort=${params.sortModel[0].sort}`
          }

          if (Object.keys(params.filterModel).length) {
            url += `&query=${params.filterModel['-'].filter}`
          }

          const res = await this.$http.get<OrdersPaginated>(url)
          this.rowData = res.data.map((order) => Order.from(order))
          this.gridApi?.hideOverlay()

          const lastRow = res.total
          // call the success callback
          params.successCallback(this.rowData, lastRow)
        }
      }
      return dataSource
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      const savedState = localStorage.getItem('orders-column-state')
      if (savedState) {
        this.gridApi?.applyColumnState({ state: JSON.parse(savedState), applyOrder: true })
      }
      if (this.selectedValue == 'quickView') {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
        this.gridApi?.setGridOption('serverSideSortOnServer', true)
      } else if (this.selectedValue == 'allItems') {
        this.gridApi?.setGridOption('serverSideSortOnServer', false)
        this.updateRowData()
      }
    },

    onColumnStateChanged(event: any) {
      if (!event.finished) return
      const state = this.gridApi?.getColumnState()
      if (state) localStorage.setItem('orders-column-state', JSON.stringify(state))
    },

    filterItems() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({ '-': { filter: this.searchTerm } })
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    },

    async delete(orders: Order[]) {
      if (confirm(this.$t('views.common.listView.selectedRows.deleteConfirm'))) {
        try {
          await this.$http.delete(Order.ENDPOINT, { data: orders })
          await this.updateRowData()
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.deleteSuccess')
          })
        } catch (error) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    },
    onCellDoubleClicked(event: any) {
      this.$router.push(`/sales/orders/order/${event.data.id}`)
    },
    newSale() {
      handleNewSale(this.t);
      this.$router.push(this.$Route.SALES_NEW_SALE);
    }
  }
})
</script>

<style></style>
